"use client";

/**
 * Client body for the TOPdesk integration page. Lives apart from `page.tsx`
 * (which stays a server component for `metadata`) so it can call `useT()` and
 * follow the visitor's locale, exactly like the Freshdesk page. All copy comes
 * from the `intgTopdesk` namespace; structure (icons, hrefs, capability
 * status) stays here.
 */

import {
  Ticket,
  RefreshCw,
  Reply,
  StickyNote,
  UserCheck,
  Search,
  History,
} from "lucide-react";
import { MarketingShell } from "@/components/marketing/marketing-shell";
import {
  UseCaseHero,
  Section,
  SectionHeader,
  FeatureGrid,
  CapabilityGrid,
  Faq,
  SecurityNote,
  RelatedLinks,
  CtaBanner,
} from "@/components/marketing/marketing-kit";
import { useT } from "@/i18n";

export function TopdeskContent() {
  const t = useT();
  const k = (s: string) => `intgTopdesk.${s}`;

  const features = [
    { key: "createLink", icon: <Ticket size={18} /> },
    { key: "statusReadBack", icon: <RefreshCw size={18} /> },
    { key: "approvedReplies", icon: <Reply size={18} /> },
    { key: "internalNotes", icon: <StickyNote size={18} /> },
    { key: "callerLink", icon: <UserCheck size={18} /> },
    { key: "supportContext", icon: <Search size={18} /> },
  ].map((f) => ({
    icon: f.icon,
    title: t(k(`features.${f.key}.title`)),
    body: t(k(`features.${f.key}.body`)),
  }));

  const capabilities = [
    { key: "readIncidents", status: "supported" as const },
    { key: "createLink", status: "supported" as const },
    { key: "callerReply", status: "conditional" as const },
    { key: "internalNote", status: "conditional" as const },
    { key: "statusPush", status: "conditional" as const },
    { key: "callerLink", status: "conditional" as const },
    { key: "inboundWebhook", status: "conditional" as const },
    { key: "notMirrored", status: "unavailable" as const },
  ].map((c) => ({
    name: t(k(`capabilities.${c.key}.name`)),
    status: c.status,
    body: t(k(`capabilities.${c.key}.body`)),
  }));

  const faq = ["q1", "q2", "q3", "q4", "q5"].map((q) => ({
    q: t(k(`faq.${q}.q`)),
    a: t(k(`faq.${q}.a`)),
  }));

  return (
    <MarketingShell>
      <UseCaseHero
        eyebrow={t(k("hero.eyebrow"))}
        title={t(k("hero.title"))}
        highlight={t(k("hero.highlight"))}
        subtitle={t(k("hero.subtitle"))}
        primary={{ href: "/demo", label: t(k("hero.primaryCta")) }}
        secondary={{ href: "/use-cases/freshdesk-jira-approved-replies", label: t(k("hero.secondaryCta")) }}
        bullets={[t(k("hero.bullet1")), t(k("hero.bullet2")), t(k("hero.bullet3"))]}
      />

      <Section>
        <SectionHeader eyebrow={t(k("what.eyebrow"))} title={t(k("what.title"))} />
        <FeatureGrid features={features} />
      </Section>

      <Section muted>
        <SectionHeader
          eyebrow={t(k("capabilities.eyebrow"))}
          title={t(k("capabilities.title"))}
          description={t(k("capabilities.description"))}
        />
        <CapabilityGrid
          capabilities={capabilities}
          statusLabels={{
            supported: t("mk.cap.supported"),
            conditional: t("mk.cap.conditional"),
            unavailable: t("mk.cap.unavailable"),
          }}
        />
      </Section>

      <Section>
        <SectionHeader eyebrow={t(k("governed.eyebrow"))} title={t(k("governed.title"))} />
        <SecurityNote icon={<History size={18} />} title={t(k("governed.noteTitle"))}>
          {t(k("governed.noteBody"))}
        </SecurityNote>
      </Section>

      <Section muted>
        <SectionHeader eyebrow={t(k("faq.eyebrow"))} title={t(k("faq.title"))} />
        <Faq items={faq} />
      </Section>

      <Section>
        <RelatedLinks
          title={t("mk.relatedTitle")}
          links={[
            {
              href: "/integrations/freshdesk",
              label: t(k("related.freshdesk.label")),
              description: t(k("related.freshdesk.description")),
            },
            {
              href: "/integrations/odoo",
              label: t(k("related.odoo.label")),
              description: t(k("related.odoo.description")),
            },
            {
              href: "/use-cases/freshdesk-jira-approved-replies",
              label: t(k("related.approvedReplies.label")),
              description: t(k("related.approvedReplies.description")),
            },
          ]}
        />
      </Section>

      <CtaBanner
        title={t(k("cta.title"))}
        description={t(k("cta.description"))}
        primary={{ href: "/demo", label: t(k("cta.primary")) }}
        secondary={{ href: "/contact", label: t(k("cta.secondary")) }}
      />
    </MarketingShell>
  );
}
